import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from '@/utils/merge-class-names';

export interface Tab<K extends string = string> {
  key: K;
  label: string;
  show?: boolean;
}

interface Props<K extends string = string> {
  className?: string;
  tabs: Tab<K>[];
  defaultActiveTab?: K;
  onClickTab: (key: K) => void;
}

export default function TabList<K extends string = string>({
  tabs,
  defaultActiveTab,
  onClickTab,
  className,
}: Props<K>) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(defaultActiveTab ?? tabs[0]?.key ?? '');

  const handleChange = (key: K) => {
    setActiveTab(key);
    onClickTab(key);
  };

  return (
    <div
      className={cn('h-[34px] flex justify-evenly border-b border-secondary', className)}
      role='tablist'>
      {tabs
        .filter((item) => item.show ?? true)
        .map((item) => (
          <button
            type='button'
            key={item.key}
            className={cn(
              'border-b-2 border-b-transparent transition-colors flex flex-col justify-start text-secondary',
              {
                'border-b-primary text-primary font-bold': item.key === activeTab,
              },
            )}
            onClick={() => handleChange(item.key)}>
            {t(item.label)}
          </button>
        ))}
    </div>
  );
}
